/**
 * Leadership Overview block — fresh redesign.
 *
 * Three cards in the middle row got rebuilt with new visual patterns
 * + real-estate-native vocabulary:
 *
 *   Top Closers & Key Contributors  →  Top Performers
 *     Podium for top 3 agents (gold / silver / bronze tints) with a
 *     quiet runner-up list below.
 *
 *   New Lead Intake                 →  New Inquiries
 *     3 compact rows — Total Inquiries / Database Leads / Walk-ins —
 *     with progress bars showing each row's share of the total.
 *
 *   Reminder                        →  Today's Schedule
 *     Vertical timeline feed of upcoming site visits + meetings.
 *     Status chip strip moved up so the count of Pending Calls /
 *     Cold / Broker / Warm sits above the calendar.
 *
 * Glue: the Marketing Leads overview + Revenue widget + Agent
 * Talktime panel keep their existing positions but the block as a
 * whole feels much more dynamic because every panel now has a
 * distinct visual identity instead of "white card with title".
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Trophy, Medal, Award, TrendingUp, TrendingDown, Clock,
  Inbox, Database, Compass, Radar, Calendar, ArrowRight,
} from 'lucide-react';
import MarketingLeadsOverview from './MarketingLeadsOverview';
import AgentTalktimePanel from './AgentTalktimePanel';
// Revenue widget is rendered separately on Dashboard.js (right below
// the Quick Start Meeting) and CEODashboard.js (after the scope bar),
// so it intentionally isn't included in this composite block anymore.
// Removing it here prevents duplicate rendering on those pages.
import HelpInfo from './HelpInfo';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (n) => {
  if (n == null) return '0';
  return Number(n).toLocaleString();
};

const fmtAED = (n) => {
  if (!n || Number.isNaN(Number(n))) return 'AED 0';
  const v = Number(n);
  if (v >= 1_000_000) return `AED ${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000)     return `AED ${(v / 1_000).toFixed(1)}K`;
  return `AED ${v.toLocaleString('en-AE')}`;
};

const initials = (name) => {
  const parts = String(name || '').trim().split(/\s+/);
  if (!parts.length) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const initialsBg = (name) => {
  const palette = [
    'bg-slate-200 text-slate-700',
    'bg-violet-200 text-violet-700',
    'bg-amber-200 text-amber-700',
    'bg-emerald-200 text-emerald-700',
    'bg-rose-200 text-rose-700',
    'bg-blue-200 text-blue-700',
  ];
  let h = 0;
  for (const c of String(name || '')) h = (h + c.charCodeAt(0)) % palette.length;
  return palette[h];
};

const prettyRole = (r) =>
  !r ? 'Agent' : String(r).replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const Momentum = ({ pct }) => {
  if (pct == null) return null;
  const flat = pct === 0;
  const up = pct > 0;
  const cls = flat
    ? 'text-slate-400'
    : up
      ? 'text-emerald-600'
      : 'text-rose-500';
  return (
    <span className={`inline-flex items-center gap-0.5 text-[11px] font-semibold ${cls}`}>
      {!flat && (up ? <TrendingUp size={10} /> : <TrendingDown size={10} />)}
      {flat ? 'flat' : `${up ? '+' : ''}${pct}%`}
    </span>
  );
};

// ============================================================
// MVP Board — podium + runner-up list
// ============================================================
const PodiumSlot = ({ rank, closer, tint, glow }) => {
  const Icon = rank === 1 ? Trophy : rank === 2 ? Medal : Award;
  return (
    <div className={`relative rounded-xl p-3 text-center bg-gradient-to-br ${tint} border ${glow}`}>
      <div className="absolute -top-2 left-1/2 -translate-x-1/2">
        <div className={`w-7 h-7 rounded-full flex items-center justify-center shadow-sm
                        ${rank === 1 ? 'bg-amber-400 text-white' : rank === 2 ? 'bg-slate-300 text-slate-700' : 'bg-orange-300 text-orange-900'}`}>
          <Icon size={13} />
        </div>
      </div>
      <Avatar className="w-10 h-10 mx-auto mt-3">
        <AvatarFallback className={`text-[11px] font-bold ${initialsBg(closer?.agent_name)}`}>
          {initials(closer?.agent_name || '?')}
        </AvatarFallback>
      </Avatar>
      <p className="text-[12px] font-semibold text-slate-900 truncate mt-2">
        {closer?.agent_name || '—'}
      </p>
      <p className="text-[10px] text-slate-500 truncate">{prettyRole(closer?.agent_role)}</p>
      <p className="text-base font-bold text-slate-900 mt-1.5">
        {closer?.deals_won || 0}
        <span className="text-[10px] text-slate-400 ml-1">wins</span>
      </p>
      <p className="text-[10px] text-emerald-600 font-medium">{fmtAED(closer?.revenue || 0)}</p>
    </div>
  );
};

const MVPBoardCard = ({ closers = [], loading }) => {
  const [rankBy, setRankBy] = useState('deals_won');
  const sorted = useMemo(() => {
    return [...closers].sort((a, b) =>
      rankBy === 'revenue'
        ? (b.revenue || 0) - (a.revenue || 0)
        : (b.deals_won || 0) - (a.deals_won || 0)
    );
  }, [closers, rankBy]);

  return (
    <div className="relative rounded-2xl p-5 h-full overflow-hidden
                    bg-gradient-to-br from-amber-50/80 via-white to-orange-50/40
                    border border-amber-100/70
                    shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
      <div className="absolute -top-10 -right-10 w-32 h-32 rounded-full bg-amber-200/40 blur-3xl" />
      <div className="relative">
        <div className="flex items-start justify-between gap-2 mb-4">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center text-white shadow-sm">
              <Trophy size={16} />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-slate-900">Top Performers</h3>
              <p className="text-[11px] text-slate-500">Highest closers this week</p>
            </div>
            <HelpInfo
              align="inline"
              title="Top Performers"
              steps={[
                'The top three agents in the current window — ranked by deals closed (SPA signed).',
                'The runner-up list shows the next three; check it when planning who to mentor or to put on a bigger inventory next month.',
                'Switch Rank to Revenue to see who\'s bringing in the biggest ticket sizes rather than the most deals.',
              ]}
              tip="If the same agent doesn't show up across two consecutive weeks, that's a coaching trigger before momentum dies."
            />
          </div>
          <Select value={rankBy} onValueChange={setRankBy}>
            <SelectTrigger className="h-7 text-xs px-2.5 gap-1.5 w-[110px] bg-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="deals_won">Rank: Wins</SelectItem>
              <SelectItem value="revenue">Rank: Revenue</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {loading ? (
          <div className="grid grid-cols-3 gap-2">
            {[0,1,2].map((i) => <Skeleton key={i} className="h-32 rounded-xl" />)}
          </div>
        ) : sorted.length === 0 ? (
          <div className="text-center py-8 rounded-xl border border-dashed border-amber-200 bg-amber-50/30">
            <Trophy size={28} className="text-amber-300 mx-auto" strokeWidth={1.5} />
            <p className="text-sm font-semibold text-slate-800 mt-2">Podium is empty</p>
            <p className="text-[11px] text-slate-500 mt-1">
              First closed deal of the week claims the top spot.
            </p>
          </div>
        ) : (
          <>
            {/* Podium — slot 2 on the left (shorter), slot 1 in the
                middle (tallest), slot 3 on the right. Mirrors the
                way a real podium looks. */}
            <div className="grid grid-cols-3 gap-2 items-end pt-3">
              {sorted[1] && (
                <PodiumSlot
                  rank={2}
                  closer={sorted[1]}
                  tint="from-slate-100 to-slate-200/60"
                  glow="border-slate-200"
                />
              )}
              {sorted[0] && (
                <div className="-translate-y-2">
                  <PodiumSlot
                    rank={1}
                    closer={sorted[0]}
                    tint="from-amber-100 to-amber-200/60"
                    glow="border-amber-300 ring-2 ring-amber-200/50"
                  />
                </div>
              )}
              {sorted[2] && (
                <PodiumSlot
                  rank={3}
                  closer={sorted[2]}
                  tint="from-orange-100 to-orange-200/60"
                  glow="border-orange-200"
                />
              )}
            </div>

            {/* Runner-up list — ranks 4-6 if present */}
            {sorted.length > 3 && (
              <ul className="mt-4 space-y-1.5 pt-3 border-t border-slate-100">
                {sorted.slice(3, 6).map((c, i) => (
                  <li key={c.agent_id || i} className="flex items-center gap-2.5 py-1 group">
                    <span className="text-[10px] font-mono text-slate-400 w-5 text-right">#{i + 4}</span>
                    <Avatar className="w-6 h-6">
                      <AvatarFallback className={`text-[9px] font-semibold ${initialsBg(c.agent_name)}`}>
                        {initials(c.agent_name)}
                      </AvatarFallback>
                    </Avatar>
                    <span className="text-xs text-slate-800 flex-1 truncate group-hover:text-slate-900">
                      {c.agent_name}
                    </span>
                    <span className="text-xs font-semibold text-slate-900 tabular-nums">{c.deals_won}</span>
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
      </div>
    </div>
  );
};

// ============================================================
// Intake Pulse — ticker rows with progress bars
// ============================================================
const INTAKE_ROWS = [
  { key: 'total',  label: 'Total Inquiries',  icon: Inbox,    tone: 'violet'  },
  { key: 'data',   label: 'Database Leads',   icon: Database, tone: 'indigo'  },
  { key: 'direct', label: 'Walk-ins & Direct', icon: Compass, tone: 'emerald' },
];

const INTAKE_TONE = {
  violet:  { bar: 'bg-violet-500',  bg: 'bg-violet-50',  fg: 'text-violet-600' },
  indigo:  { bar: 'bg-indigo-500',  bg: 'bg-indigo-50',  fg: 'text-indigo-600' },
  emerald: { bar: 'bg-emerald-500', bg: 'bg-emerald-50', fg: 'text-emerald-600' },
};

const IntakePulseCard = ({ intake, loading }) => {
  const total = intake?.total?.value || 0;
  const days = intake?.window_days || 7;
  return (
    <div className="relative rounded-2xl p-5 h-full overflow-hidden
                    bg-gradient-to-br from-violet-50/60 via-white to-indigo-50/40
                    border border-violet-100/70
                    shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
      <div className="absolute -top-10 -right-10 w-32 h-32 rounded-full bg-violet-200/30 blur-3xl" />
      <div className="relative">
        <div className="flex items-start gap-2 mb-4 flex-wrap">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-violet-500 to-indigo-600 flex items-center justify-center text-white shadow-sm">
            <Radar size={16} />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-slate-900">New Inquiries</h3>
            <p className="text-[11px] text-slate-500">Last {days} days · how buyers are reaching us</p>
          </div>
          <HelpInfo
            align="inline"
            title="New Inquiries"
            steps={[
              <><span className="font-medium">Total Inquiries</span> — every new buyer in the window, across every channel.</>,
              <><span className="font-medium">Database Leads</span> — bulk-imported records (purchased lists, broker hand-offs, manual entry).</>,
              <><span className="font-medium">Walk-ins & Direct</span> — buyers who walked into a property or contacted the office directly.</>,
              'Each bar shows that source\'s share of total inquiries.',
            ]}
            tip="A high Database Leads bar with low Walk-ins usually means the team is working old data; flag it before the database goes cold."
          />
        </div>

        {loading ? (
          <div className="space-y-3">
            {[0,1,2].map((i) => <Skeleton key={i} className="h-14 w-full rounded-lg" />)}
          </div>
        ) : (
          <div className="space-y-3">
            {INTAKE_ROWS.map((row) => {
              const data = intake?.[row.key];
              const value = data?.value || 0;
              const pct = total ? (value / total) * 100 : 0;
              const tone = INTAKE_TONE[row.tone];
              const Icon = row.icon;
              return (
                <div key={row.key}>
                  <div className="flex items-center gap-2 mb-1">
                    <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${tone.bg} ${tone.fg}`}>
                      <Icon size={13} />
                    </div>
                    <span className="text-[12px] font-medium text-slate-700 flex-1">{row.label}</span>
                    <Momentum pct={data?.delta_pct} />
                    <span className="text-base font-bold text-slate-900 tabular-nums w-10 text-right">
                      {fmt(value)}
                    </span>
                  </div>
                  <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden ml-9">
                    <div
                      className={`h-full rounded-full ${tone.bar} transition-all`}
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

// ============================================================
// On Deck — vertical timeline feed of upcoming events
// ============================================================
const DECK_DOTS = [
  { key: 'open_not_connected', label: 'Pending Call', dot: 'bg-emerald-500' },
  { key: 'cold',               label: 'Cold',         dot: 'bg-orange-400'  },
  { key: 'broker',             label: 'Broker',       dot: 'bg-sky-400'     },
  { key: 'warm',               label: 'Warm',         dot: 'bg-rose-400'    },
];

const OnDeckCard = ({ reminders, loading }) => {
  const counts = reminders?.status_counts || {};
  const events = reminders?.events        || [];
  const [days, setDays] = useState(7);
  return (
    <div className="relative rounded-2xl p-5 h-full overflow-hidden
                    bg-gradient-to-br from-rose-50/60 via-white to-pink-50/40
                    border border-rose-100/70
                    shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
      <div className="absolute -top-10 -right-10 w-32 h-32 rounded-full bg-rose-200/30 blur-3xl" />
      <div className="relative">
        <div className="flex items-start justify-between gap-2 mb-3 flex-wrap">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-rose-500 to-pink-500 flex items-center justify-center text-white shadow-sm">
              <Clock size={16} />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-slate-900">Today's Schedule</h3>
              <p className="text-[11px] text-slate-500">Site visits, calls & meetings on the calendar</p>
            </div>
            <HelpInfo
              align="inline"
              title="Today's Schedule"
              steps={[
                'The chip strip at the top shows current lead counts — buyers waiting on a call, cold leads, broker leads, and warm leads.',
                <>The timeline below lists upcoming <span className="font-medium">site visits, calls and meetings</span> in the chosen window, sorted by time.</>,
                'Each entry shows the scheduled time, the agent owning it, and which buyer the appointment is with.',
              ]}
              tip="An empty calendar with a high Cold count means the team should be re-engaging cold buyers — block time tomorrow for outreach calls."
            />
          </div>
          <Select value={String(days)} onValueChange={(v) => setDays(parseInt(v, 10))}>
            <SelectTrigger className="h-7 text-xs px-2.5 gap-1.5 w-[120px] bg-white">
              <Calendar size={11} className="text-slate-500 mr-1" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="1">Today</SelectItem>
              <SelectItem value="7">Next 7 Days</SelectItem>
              <SelectItem value="14">Next 14 Days</SelectItem>
              <SelectItem value="30">Next 30 Days</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Status chips — moved up here as a single horizontal strip
            since they're a quick "context" read, not the headline. */}
        {loading ? (
          <Skeleton className="h-9 w-full mb-4 rounded-lg" />
        ) : (
          <div className="flex flex-wrap gap-1.5 mb-4 pb-3 border-b border-slate-100">
            {DECK_DOTS.map((d) => (
              <span
                key={d.key}
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white border border-slate-200 text-[11px]"
              >
                <span className={`w-1.5 h-1.5 rounded-full ${d.dot}`} />
                <span className="text-slate-600">{d.label}</span>
                <span className="font-bold text-slate-900 tabular-nums">{fmt(counts[d.key])}</span>
              </span>
            ))}
          </div>
        )}

        {/* Timeline feed */}
        {loading ? (
          <div className="space-y-3">
            {[0,1,2].map((i) => <Skeleton key={i} className="h-12 w-full rounded-lg" />)}
          </div>
        ) : events.length === 0 ? (
          <div className="text-center py-8 rounded-xl border border-dashed border-rose-200 bg-rose-50/30">
            <p className="text-sm font-medium text-slate-700">Calendar's clear</p>
            <p className="text-[11px] text-slate-500 mt-1">No site visits or meetings scheduled.</p>
          </div>
        ) : (
          <ol className="relative border-l-2 border-slate-200 ml-1.5 space-y-3 max-h-[280px] overflow-auto pr-1">
            {events.slice(0, 8).map((e, i) => {
              const d = e.when ? new Date(e.when) : null;
              const when = d ? d.toLocaleString('en-AE', {
                timeZone: 'Asia/Dubai',
                month: 'short', day: '2-digit',
                hour: '2-digit', minute: '2-digit',
              }) : '';
              return (
                <li key={e.id || `${i}-${e.when}`} className="ml-3 group cursor-pointer">
                  <span className="absolute -left-[5px] mt-1.5 w-2.5 h-2.5 rounded-full bg-rose-500 ring-2 ring-rose-100 group-hover:ring-rose-200" />
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-[11px] text-slate-400">{when}</p>
                    <ArrowRight size={11} className="text-slate-300 group-hover:text-rose-400 transition" />
                  </div>
                  <p className="text-[13px] font-semibold text-slate-900 truncate">
                    {e.agent_name || 'Unassigned'}
                  </p>
                  <p className="text-[11px] text-slate-500 truncate">
                    Site visit with <span className="text-slate-700">{e.target_name || e.title || 'buyer'}</span>
                  </p>
                </li>
              );
            })}
          </ol>
        )}
      </div>
    </div>
  );
};

// ============================================================
// Block
// ============================================================
export default function LeadershipOverviewBlock({
  // Operational sub-panel toggle. Defaults to true so existing consumers
  // (CEODashboard.js, anywhere else) keep their current behaviour. The
  // /dashboard page passes false when in Strategic view to suppress the
  // per-agent call panel.
  showAgentTalktime = true,
} = {}) {
  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/leadership/overview`, {
        params: { token, days: 7 },
      });
      setData(r.data?.data || null);
    } catch (e) {
      console.warn('leadership-overview:', e?.response?.data?.detail || e);
    } finally {
      setLoading(false);
    }
  }, [token]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-4">
      <MarketingLeadsOverview />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <MVPBoardCard      closers={data?.top_closers} loading={loading} />
        <IntakePulseCard   intake={data?.lead_intake}  loading={loading} />
        <OnDeckCard        reminders={data?.reminders} loading={loading} />
      </div>

      {showAgentTalktime && <AgentTalktimePanel />}
    </div>
  );
}
